/** @jsxImportSource @emotion/react */

import { Fragment, useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';

import {
  CLUB,
  CLUB_CASUAL,
  DONT_MIND,
  KICKABOUT_CASUAL,
  PAGE_NUMBER,
  PAGE_SIZE,
  RECOMMENDATION_PAGE,
  RECOMMENDATIONS_PAGE_TITLE_MAPPING,
} from '../../const';
import { usePreferencesContext } from '../../context/preferences';
import { useRecommendations } from '../../context/recommendations';
import { useScrollDirectionContext } from '../../context/scrollDirection';
import { useSetHeaderHeightContext } from '../../context/setHeaderHeight';
import useErrorBoundary from '../../hooks/useErrorBoundary';
import useImage from '../../hooks/useImage';
import usePageLoadInfo from '../../hooks/usePageLoadInfo';
import { getRecommendations, getRecommendationsPageData } from '../../services';
import CardListView from '../CardListView';
import CFAContactCountyBNR from '../CFAContactCountyBNR';
import CFADivider from '../CFADivider';
import CFAOpenFiltersMSG from '../CFAOpenFiltersMSG';
import FilterListMapView from '../FilterListMapView';
import FiltersMenu from '../FiltersMenu';
import FootballTypeTabs from '../FootballTypeTabs';
import Footer from '../Footer';
import Loader from '../Loader';
import LoadMoreResultsButton from '../LoadMoreResultsButton';
import NumOfOpportunities from '../NumOfOpportunities';
import RecommendationCardLoading from '../RecommendationCardLoading';
import RecommendationsHero from '../RecommendationsHero';
import TabPanel from '../TabPanel';
import useStyles from './styles';

const SetImagePath = (data, FootballType, imgData) => {
  const dataKey = Object.keys(data.hero);
  dataKey.forEach(detailKey => {
    if (FootballType === parseInt(detailKey)) {
      data.hero[
        detailKey
      ].images.desktop.src = `/images/desktop/recommendations/hero/Hero.jpg`;

      data.hero[
        detailKey
      ].images.mobile.src = `/images/mobile/recommendations/hero/Hero.jpg`;
    }
  });
  return data;
};

const RecommendationsPage = () => {
  const styles = useStyles();
  const throwError = useErrorBoundary();
  const [pageData, setPageData] = useState();
  const [filterShowHide, setFilterShowHide] = useState(false);
  // const [footballTypeFilter, setFootballTypeFilter] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef();
  const loadingRefFilter = useRef();
  const [isLoadingFilter, setIsLoadingFilter] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { preferences } = usePreferencesContext();
  const {
    FootballType,
    SelectedFootballType: selectedFootballType,
    quickSearch,
  } = preferences || {};
  const {
    clubRecommendations,
    casualRecommendations,
    clubCasualRecommendations,
    setRecommendations,
  } = useRecommendations();
  // to get dynamic image url path
  const imagePath = useImage();
  const { scrolldirectionContext } = useScrollDirectionContext();
  const { headerHeight } = useSetHeaderHeightContext();
  const [numOfOpportunities, setNumOfOpportunities] = useState(0);
  const noOpportunities =
    numOfOpportunities === 0 ||
    numOfOpportunities === null ||
    !numOfOpportunities;

  const SHOWN_INCREMENT = 12;
  const [visibleCount, setVisibleCount] = useState(SHOWN_INCREMENT);
  // ✅ ADDED: Ref for focusing the first new card
  const firstNewCardRef = useRef(null);
  let recommendations = null;

  if (selectedFootballType === CLUB) {
    recommendations = clubRecommendations;
  } else if (selectedFootballType === KICKABOUT_CASUAL) {
    recommendations = casualRecommendations;
  } else if (selectedFootballType === CLUB_CASUAL) {
    recommendations = clubCasualRecommendations;
  }
  // when we save we want to send the focus to the loader
  // so that it is announced to screen readers
  useEffect(() => {
    if (isLoadingFilter) {
      loadingRefFilter.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isLoadingFilter, loadingRefFilter]);
  const totalCards = recommendations?.RecommendationClubCentreCartDto || [];
  const visibleCardsForDivider = totalCards.slice(0, visibleCount);

  // set the page title based on user preference of football type.
  usePageLoadInfo(
    'recommendations',
    `${RECOMMENDATIONS_PAGE_TITLE_MAPPING[FootballType]}`
  );
  useEffect(() => {
    // Using this so that if a User visits club through direct link and opens a team
    // User should be able to get a back to club button on that team's hero section
    // Save the current club URL in sessionStorage
    sessionStorage.setItem('backLinkResults', window.location.href);
  }, []);

  useEffect(() => {
    setNumOfOpportunities(recommendations.CntOpportunity);
  }, [recommendations]);

  useEffect(() => {
    getRecommendationsPageData()
      .then(({ data }) => {
        if (data) {
          // set dynamic src for hero images
          data = SetImagePath(data, FootballType, imagePath);
          setPageData({ ...data });
        }
      })
      .catch(throwError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFootballType, imagePath]);

  useEffect(() => {
    if (!recommendations && !!selectedFootballType) {
      const previouslyFocusedElement = document.activeElement;
      setIsLoading(true);

      Promise.all([
        getRecommendations(selectedFootballType, preferences).catch(throwError),
      ]).then(data => {
        if (data) {
          const recommendationRecords = data[0].data; // recommendation data
          setRecommendations(recommendationRecords, selectedFootballType);

          setIsLoading(false);

          setTimeout(() => {
            // if the user focuses on the loading message
            // we want to focus back on the previously focused element
            if (loadingRef.current?.contains(document.activeElement)) {
              previouslyFocusedElement?.focus();
            }
          }, 100);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preferences,
    selectedFootballType,
    setRecommendations,
    recommendations,
    throwError,
  ]);

  useEffect(() => {
    localStorage.setItem('pathInfomation', window.location.pathname);
  }, []);

  if (!selectedFootballType) {
    return <Redirect to="/" />;
  }

  const showNext = () => {
    setVisibleCount(visibleCount + SHOWN_INCREMENT);
  };

  const getMoreResults = () => {
    if (!recommendations) {
      return;
    }
    setIsLoadingMore(true);
    getRecommendations(selectedFootballType, {
      ...preferences,
      // get the page after the current one
      [PAGE_NUMBER]: Math.floor(visibleCount / preferences[PAGE_SIZE]) + 1,
    })
      //])
      .then(({ data }) => {
        if (data) {
          const recommendationRecords = data; // recommendation data
          const updatedRecommendations = {
            ...recommendations,
            RecommendationClubCentreCartDto: [
              ...recommendations.RecommendationClubCentreCartDto,
              ...recommendationRecords.RecommendationClubCentreCartDto,
            ],
            CntOpportunity:
              recommendationRecords.CntOpportunity ||
              recommendations.CntOpportunity,
          };

          setRecommendations(updatedRecommendations, selectedFootballType);
          setIsLoadingMore(false);
          showNext();
          // ✅ ADDED: Focus management after results load
          setTimeout(() => {
            if (firstNewCardRef.current) {
              // Focus the first new card element to maintain tab flow
              firstNewCardRef.current.focus();
            }
          }, 50);
        }
      })
      .catch(throwError);
  };

  const onResetVisibleCount = () => {
    setVisibleCount(SHOWN_INCREMENT);
  }

  return (
    <div css={styles.base} id="main-content">
      <div css={styles.heroWrapper}>
        {selectedFootballType && pageData?.hero && (
          <RecommendationsHero
            css={styles.hero}
            textSize={3}
            {...pageData.hero[FootballType]}
            isOnBoardingCriteriaIncluded={true}
            scrolldirectionContext={scrolldirectionContext}
            headerHeight={headerHeight}
            isRec={true}
            ctaText="Start a new search"
            quickSearch={quickSearch}
          />
        )}
          <FilterListMapView
            filterShowHide={filterShowHide}
            setFilterShowHide={setFilterShowHide}
          />
      </div>      
      <div css={styles.wrapper}>
        {FootballType === DONT_MIND && <FootballTypeTabs />}
        <TabPanel selectedType={selectedFootballType} id={selectedFootballType}>
          {isLoading && (
            <RecommendationCardLoading
              selectedType={selectedFootballType}
              ref={loadingRef}
            />
          )}
          <FiltersMenu  
            filterShowHide={filterShowHide}
            page={RECOMMENDATION_PAGE}
            onLoadingChange={loading => setIsLoadingFilter(loading)}
            setFilterShowHide={setFilterShowHide}
            numOfOpportunities={numOfOpportunities}
            onResetVisibleCount={onResetVisibleCount}
          />
          {isLoadingFilter ? (
            <Loader
              message="Loading results"
              ref={loadingRefFilter}
              isOnboarding="true"
              isFilter="true"
              tabIndex="-1"
            />
          ) : (
            numOfOpportunities > 0 && (
              <Fragment>
                <div css={styles.mainContainer(filterShowHide)}>
                  <NumOfOpportunities numOfOpportunities={numOfOpportunities} />
                  <div css={styles.cardContainer(filterShowHide)}>
                    {visibleCardsForDivider.map((item, index) => {
                      // ✅ ADDED: Determine if this is the first card of the new batch
                      const isFirstNewCard =
                        index === visibleCount - SHOWN_INCREMENT;

                      return (
                        <Fragment key={index}>
                          <CardListView
                            key={index}
                            index={index}
                            cardType={
                              item.FootballType === CLUB ? 'club' : 'sessional'
                            }
                            // ✅ ADDED: Conditionally apply the ref
                            ref={isFirstNewCard ? firstNewCardRef : null}
                            {...item}
                          />
                          {(index + 1) % 24 === 0 && (
                            <CFADivider filterShowHide={filterShowHide} />
                          )}
                        </Fragment>
                      );
                    })}
                  </div>

                  {isLoadingMore ? (
                    <Loader
                      inline
                      message={`Loading more results`}
                      css={styles.loadMore}
                      ref={loadingRef}
                    />
                  ) : (
                    visibleCount < numOfOpportunities && (
                      <LoadMoreResultsButton onClick={getMoreResults} />
                    )
                  )}
                </div>
              </Fragment>
            )
          )}

          {!isLoadingFilter && noOpportunities && (
            <div css={styles.noOppOpenFilterDiv(filterShowHide)}>
              <NumOfOpportunities numOfOpportunities={numOfOpportunities} />
              <CFAOpenFiltersMSG
                filterShowHide={filterShowHide}
                setFilterShowHide={setFilterShowHide}
              />
            </div>
          )}
        </TabPanel>
      </div>
      <CFAContactCountyBNR />
      <Footer />
    </div>
  );
};

export default RecommendationsPage;
